import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import { useControlledValue } from '../../molecules/FormField/useControlledValue';
import { DropzoneContextType, DropzoneProps, DropzoneStatus, UploadedFileMetadata } from './Dropzone.types';

const usePresenter = <T extends File | string = File>(props: DropzoneProps<T>) => {
  const {
    value,
    empty,
    loading,
    uploaded,
    accept,
    maxSize,
    maxFiles = 1,
    disabled,
    onValueChange,
    onFilesReset,
    id,
    drag,
    handleFilesChange,
    type = 'File',
    onUpload,
    onDelete,
    resolveMetadata,
  } = props;

  const [userSelectedFiles, setUserSelectedFiles] = useState<File[]>();
  const [metadata, setMetadata] = useState<UploadedFileMetadata[]>([]);
  
  /** Ref to track if metadata resolution is in progress to prevent race conditions */
  const isResolvingMetadataRef = useRef(false);

  const { value: files, onValueChange: handleValueChange } = useControlledValue<T[]>({
    onValueChange,
    defaultValue: [],
    value,
  });

  const [status, setStatus] = useState<DropzoneStatus>('Empty');

  // Effect to resolve metadata when value changes (e.g., when loading from saved form state)
  // This is critical for displaying uploaded files when form loads with existing data
  useEffect(() => {
    if (type === 'URL' && resolveMetadata && files && files.length > 0 && !isResolvingMetadataRef.current) {
      const resolveAllMetadata = async () => {
        isResolvingMetadataRef.current = true;
        setStatus('Loading');
        try {
          const urls = files as unknown as string[];

          // Check if we already have metadata for all URLs
          const hasAllMetadata = urls.every(url => metadata.find(m => m.url === url));

          if (!hasAllMetadata) {
            const resolvedMetadata = await resolveMetadata(urls);
            setMetadata(resolvedMetadata);

            // Update status if we have files
            if (resolvedMetadata.length > 0) {
              setStatus('Uploaded');
            }
          } else if (metadata.length > 0) {
            // We already have metadata, just ensure status is correct
            setStatus('Uploaded');
          }
        } catch (error) {
          console.error('Failed to resolve metadata:', error);
          setStatus('Empty');
        } finally {
          isResolvingMetadataRef.current = false;
        }
      };

      resolveAllMetadata();
    }
  }, [type, resolveMetadata, files, metadata]);

  const resetFiles = useCallback(async () => {
    // Call onDelete if provided and there are values to delete
    if (onDelete && files && files.length > 0) {
      await onDelete(files);
    }

    setStatus('Empty');
    setMetadata([]);
    setUserSelectedFiles(undefined);
    handleValueChange([]);
    handleFilesChange?.([]);
    onFilesReset?.();
  }, [onDelete, files, handleValueChange, handleFilesChange, onFilesReset]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUserSelectedFiles(acceptedFiles);
      setStatus('Loading');
      handleFilesChange?.(acceptedFiles);

      if (type === 'URL' && onUpload) {
        // URL mode with async upload
        try {
          const urls = await onUpload(acceptedFiles);

          // Create metadata from uploaded files
          const uploadedMetadata: UploadedFileMetadata[] = acceptedFiles.map((file, index) => ({
            url: urls[index],
            filename: file.name,
            size: file.size,
            mimetype: file.type,
          }));

          setMetadata(uploadedMetadata);
          handleValueChange(urls as T[]);
          setStatus('Uploaded');
        } catch (error) {
          console.error('Upload failed:', error);
          setStatus('Empty');
          setUserSelectedFiles(undefined);
        }
      } else if (type === 'File') {
        // File mode - update the form state immediately with File objects
        handleValueChange(acceptedFiles as T[]);
        setStatus('Uploaded');
      } else {
        // URL mode without onUpload callback - just show uploaded state
        setStatus('Uploaded');
      }
    }
  }, [type, onUpload, handleValueChange, handleFilesChange]);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    multiple: maxFiles > 1,
    accept,
    maxSize,
    maxFiles,
    noClick: true,
    disabled,
  });

  const variantProps = useBuildVariantProps(
    'dropzone',
    {
      status,
    }
  );

  const contextValue: DropzoneContextType<T> = useMemo(() => ({
    status,
    type: type || 'File',
    files: userSelectedFiles || [],
    accept: accept || {},
    selectFiles: open,
    resetFiles,
    value: files || [],
    metadata,
  }), [status, type, userSelectedFiles, accept, open, resetFiles, files, metadata]);

  return {
    variantProps,
    files,
    userSelectedFiles,
    getRootProps,
    getInputProps,
    open,
    empty,
    loading,
    uploaded,
    drag,
    status,
    accept,
    resetFiles,
    id,
    isDragActive,
    contextValue,
  };
};

export default usePresenter;